import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { logEvent } from 'firebase/analytics'
import { AnimatePresence } from 'framer-motion'

import { useLiveQuery } from 'dexie-react-hooks'
import { Backdrop, ChatContainer, Content, Footer } from './styles'
import { Header } from './Header'
import { Loader } from '../Message'
import { useChatMessagesScroll } from '../hooks/useChatMessagesScroll'
import { WelcomeMessage } from '../WelcomeMessage'
import { LLMResponseMessage } from '../LLMResponseMessage'
import { SelectedQuestionMessage } from '../SelectedQuestionMessage'
import { QuestionMenuMessage } from '../QuestionMenuMessage'
import { ErrorMessage } from '../ErrorMessage'
import { OpenQuestion } from '../OpenQuestion'
import { RatingResultsMessage } from '../RatingResultsMessage'
import { analytics } from '~/firebase-analytics'
import { useLoggedInUser, useOpenCloseState, useOpenCloseUpdater, usePatient } from '~/context'
import { useFetchMedicalNotesFormattedForLLM, usePostUserLLMPrompt } from '~/hooks'
import { Divider, Stack } from '~/styles'
import { INITIAL_MESSAGES, QUESTIONS, openQuestionTemplate } from '~/constants/llm/question'
import { IconComponent } from '~/assets/icons/component'

import { LLMChatMessage, db } from '~/data/database'

const NONE = 0

const Chat = () => {
  const { patient } = usePatient()
  const { user } = useLoggedInUser()

  const messages = useLiveQuery(() => {
    const data = db.llmChatMessages.where({ medicalRecord: patient.medicalRecord }).toArray()
    return data
  }, [patient.medicalRecord])

  const thereIsNoChatHistory = messages?.length === NONE
  const { data: medicalNotes, isLoading: isLoadingMedicalNotes } = useFetchMedicalNotesFormattedForLLM(
    { pathParam: { patientId: patient?.id } },
    { enabled: thereIsNoChatHistory }
  )

  const addMessages = async (messages: LLMChatMessage[]): Promise<void> => {
    const addMessagePromises = messages.map(async message => {
      const result = await db.llmChatMessages.add(message)
      return result
    })

    await Promise.all(addMessagePromises)
  }

  if (thereIsNoChatHistory) {
    db.llmChatMessages.clear()
    addMessages([
      {
        medicalRecord: patient.medicalRecord,
        content: INITIAL_MESSAGES[0].content,
        typeMessage: INITIAL_MESSAGES[0].typeMessage
      },
      {
        medicalRecord: patient.medicalRecord,
        content: INITIAL_MESSAGES[1].content,
        typeMessage: INITIAL_MESSAGES[1].typeMessage
      }
    ])
  }

  const { run, isLoading: isLoadingUserPrompt } = usePostUserLLMPrompt({
    onSuccess: llmResponse => {
      const llmResponseWithLineBreaks = llmResponse.output.content.replaceAll('\n', '  \n')

      addMessages([
        {
          medicalRecord: patient.medicalRecord,
          content: llmResponseWithLineBreaks,
          typeMessage: 'llm-response-message'
        },
        {
          medicalRecord: patient.medicalRecord,
          typeMessage: 'rating-results-message',
          feedbackId: llmResponse.metadata.run_id
        },
        { medicalRecord: patient.medicalRecord, typeMessage: 'question-menu-message' }
      ])
    },
    onError: () => {
      addMessages([{ medicalRecord: patient.medicalRecord, typeMessage: 'error-message' }])
    }
  })

  const { close } = useOpenCloseUpdater()
  const { currentChatRef, scrollMessageToBottom } = useChatMessagesScroll()

  const handleSelectOption = (e: React.MouseEvent) => {
    e.preventDefault()
    const selectedOption = e.currentTarget.getAttribute('value')
    if (!selectedOption || !medicalNotes) return

    addMessages([
      {
        medicalRecord: patient.medicalRecord,
        content: QUESTIONS[selectedOption].label,
        typeMessage: 'selected-question-message'
      }
    ])

    run({
      body: {
        topic: QUESTIONS[selectedOption].enhancedQuestion,
        context: medicalNotes.formattedMedicalNotes
      },
      header: {
        codLogin: user?.objeto?.[0]?.CodLogin || '',
        prompt: QUESTIONS[selectedOption].id,
        medicalRecord: patient.medicalRecord
      }
    })
    logEvent(analytics, 'gpt_chat_interaction')
  }

  const handleSendOpenQuestion = (openQuestion: string) => {
    if (!medicalNotes) return
    const customQuestionID = '5'
    addMessages([
      { medicalRecord: patient.medicalRecord, content: openQuestion, typeMessage: 'selected-question-message' }
    ])

    run({
      body: {
        topic: openQuestionTemplate(openQuestion),
        context: medicalNotes.formattedMedicalNotes
      },
      header: {
        codLogin: user?.objeto?.[0]?.CodLogin || '',
        prompt: customQuestionID,
        medicalRecord: patient.medicalRecord
      }
    })
    logEvent(analytics, 'gpt_chat_interaction')
  }

  useEffect(() => {
    scrollMessageToBottom()
  }, [messages, scrollMessageToBottom])

  return (
    <>
      <Backdrop onClick={close} />
      <ChatContainer>
        <Header />

        <Divider />

        <Content ref={currentChatRef}>
          {messages?.map(message => {
            if (message.typeMessage === 'welcome-message') {
              return <WelcomeMessage key={message.id} greeting={message.content} />
            }

            if (message.typeMessage === 'question-menu-message') {
              return (
                <QuestionMenuMessage key={message.id} disabled={isLoadingUserPrompt} onClick={handleSelectOption} />
              )
            }

            if (message.typeMessage === 'selected-question-message') {
              return <SelectedQuestionMessage key={message.id} selectedQuestion={message.content} />
            }

            if (message.typeMessage === 'llm-response-message') {
              return <LLMResponseMessage key={message.id} llmResponse={message.content} />
            }

            if (message.typeMessage === 'error-message') {
              return <ErrorMessage key={message.id} />
            }

            if (message.typeMessage === 'rating-results-message') {
              return <RatingResultsMessage key={message.id} messageId={`${message?.feedbackId}`} />
            }
          })}

          {isLoadingUserPrompt && <Loader />}

          {isLoadingMedicalNotes && (
            <Stack flex={1} justifyContent="center">
              <IconComponent name="load" iconsize={50} />
            </Stack>
          )}
        </Content>

        <Divider />

        {!isLoadingMedicalNotes && (
          <Footer>
            <OpenQuestion disabled={isLoadingUserPrompt} onSend={handleSendOpenQuestion} />
          </Footer>
        )}
      </ChatContainer>
    </>
  )
}

export const ChatLLM = () => {
  const { isOpen } = useOpenCloseState()

  const root = document.getElementById('root') as Element
  return ReactDOM.createPortal(<AnimatePresence>{isOpen && <Chat />}</AnimatePresence>, root)
}
